<template>
    <div class="p-6 max-w-4xl mx-auto">
        <div class="flex items-center justify-between mb-6">
            <div>
                <h1 class="text-2xl font-bold text-gray-800">YouCan Integration</h1>
                <p class="text-sm text-gray-500 mt-1">Connect your YouCan store to import orders automatically.</p>
            </div>
            <img src="https://youcan.shop/build/images/logo.svg" alt="YouCan" class="h-10" onerror="this.style.display='none'" />
        </div>

        <div v-if="loading" class="bg-white rounded-lg shadow p-8 text-center text-gray-500">
            Loading...
        </div>

        <div v-else>
            <div v-if="banner.show"
                 :class="['rounded-lg p-4 mb-6 border', banner.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800']">
                {{ banner.message }}
            </div>

            <div v-if="!connection.connected" class="bg-white rounded-lg shadow p-8 text-center">
                <i class="material-icons text-gray-400" style="font-size: 64px;">insert_link</i>
                <h2 class="mt-4 text-lg font-semibold text-gray-700">No store connected</h2>
                <p class="mt-2 text-sm text-gray-500">
                    Connect your YouCan store so new orders flow into RM Express automatically.
                </p>
                <button @click="connect"
                        :disabled="connecting"
                        class="mt-6 px-6 py-3 rounded bg-green-600 hover:bg-green-700 text-white font-medium disabled:opacity-50">
                    <i class="material-icons align-middle mr-1" style="font-size: 18px;">link</i>
                    {{ connecting ? 'Redirecting...' : 'Connect YouCan store' }}
                </button>
            </div>

            <div v-else>
                <div class="bg-white rounded-lg shadow p-6 mb-4">
                    <div class="flex items-start justify-between">
                        <div>
                            <div class="flex items-center gap-2">
                                <span class="inline-block h-2 w-2 rounded-full bg-green-500"></span>
                                <span class="text-sm font-semibold text-green-700 uppercase">Connected</span>
                            </div>
                            <h2 class="mt-2 text-xl font-bold text-gray-800">
                                {{ connection.store.name || 'YouCan store' }}
                            </h2>
                            <a v-if="connection.store.url"
                               :href="connection.store.url" target="_blank"
                               class="text-sm text-blue-600 hover:underline">
                                {{ connection.store.url }}
                            </a>
                            <p class="text-xs text-gray-400 mt-2">
                                Connected on {{ formatDate(connection.connectedAt) }}
                            </p>
                        </div>
                        <button @click="disconnect"
                                :disabled="disconnecting"
                                class="px-4 py-2 rounded border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50">
                            {{ disconnecting ? 'Disconnecting...' : 'Disconnect' }}
                        </button>
                    </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div class="bg-white rounded-lg shadow p-4">
                        <p class="text-xs text-gray-500 uppercase">Orders imported</p>
                        <p class="mt-1 text-2xl font-bold text-gray-800">
                            {{ connection.stats.totalOrdersImported || 0 }}
                        </p>
                    </div>
                    <div class="bg-white rounded-lg shadow p-4">
                        <p class="text-xs text-gray-500 uppercase">Last order</p>
                        <p class="mt-1 text-sm text-gray-700">
                            {{ formatDate(connection.stats.lastOrderImportedAt) || '—' }}
                        </p>
                    </div>
                    <div class="bg-white rounded-lg shadow p-4">
                        <p class="text-xs text-gray-500 uppercase">Last sync</p>
                        <p class="mt-1 text-sm text-gray-700">
                            {{ formatDate(connection.stats.lastSyncAt) || '—' }}
                        </p>
                    </div>
                </div>

                <div class="bg-white rounded-lg shadow p-6">
                    <div class="flex items-center justify-between">
                        <div>
                            <h3 class="font-semibold text-gray-800">Manual sync</h3>
                            <p class="text-sm text-gray-500 mt-1">
                                Pull recent orders from YouCan now (in addition to real-time webhooks).
                            </p>
                        </div>
                        <button @click="syncNow"
                                :disabled="syncing"
                                class="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
                            {{ syncing ? 'Syncing...' : 'Sync now' }}
                        </button>
                    </div>
                    <div v-if="lastSyncResult" class="mt-4 text-sm text-gray-600 border-t pt-3">
                        Imported: <strong>{{ lastSyncResult.imported }}</strong> ·
                        Skipped: <strong>{{ lastSyncResult.skipped }}</strong> ·
                        Failed: <strong>{{ lastSyncResult.failed }}</strong>
                        (out of {{ lastSyncResult.total }} fetched)
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from '../../../common/axios';

export default {
    name: 'YoucanIntegration',
    data() {
        return {
            loading: true,
            connecting: false,
            disconnecting: false,
            syncing: false,
            connection: { connected: false, store: {}, stats: {} },
            banner: { show: false, type: 'success', message: '' },
            lastSyncResult: null
        };
    },
    async mounted() {
        this._readQueryFlash();
        await this._loadStatus();
    },
    methods: {
        async _loadStatus() {
            this.loading = true;
            try {
                const { data } = await axios.get('youcan/status');
                if (data && data.content) {
                    this.connection = {
                        connected: !!data.content.connected,
                        store: data.content.store || {},
                        stats: data.content.stats || {},
                        connectedAt: data.content.connectedAt
                    };
                }
            } catch (e) {
                console.error('Failed to load YouCan status', e);
            } finally {
                this.loading = false;
            }
        },
        _readQueryFlash() {
            const status = this.$route.query.status;
            const reason = this.$route.query.reason;
            if (status === 'success') {
                this.banner = { show: true, type: 'success', message: 'YouCan store connected successfully.' };
            } else if (status === 'error') {
                this.banner = { show: true, type: 'error', message: 'Connection failed: ' + (reason || 'unknown error') };
            }
            if (status) {
                this.$router.replace({ query: {} }).catch(() => {});
            }
        },
        async connect() {
            this.connecting = true;
            try {
                const { data } = await axios.get('youcan/authorize');
                const url = data && data.content && data.content.authorizeUrl;
                if (!url) throw new Error('No authorize URL returned');
                window.location.href = url;
            } catch (e) {
                console.error(e);
                window.alert && window.alert('Could not start YouCan authorization', 'error');
                this.connecting = false;
            }
        },
        async disconnect() {
            if (!confirm('Disconnect your YouCan store? Orders will stop syncing.')) return;
            this.disconnecting = true;
            try {
                await axios.post('youcan/disconnect');
                this.connection = { connected: false, store: {}, stats: {} };
                this.banner = { show: true, type: 'success', message: 'YouCan store disconnected.' };
            } catch (e) {
                console.error(e);
                window.alert && window.alert('Failed to disconnect', 'error');
            } finally {
                this.disconnecting = false;
            }
        },
        async syncNow() {
            this.syncing = true;
            try {
                const { data } = await axios.post('youcan/syncOrders', {});
                this.lastSyncResult = data && data.content;
                await this._loadStatus();
            } catch (e) {
                console.error(e);
                window.alert && window.alert('Sync failed', 'error');
            } finally {
                this.syncing = false;
            }
        },
        formatDate(d) {
            if (!d) return null;
            try { return new Date(d).toLocaleString(); } catch (e) { return d; }
        }
    }
};
</script>
